import Chip from "@mui/material/Chip";
import { alpha, styled } from "@mui/material/styles";
import TruncatedCell from "_shared/_views/tables/truncated-cell";

/**
 * The four things a status can say about a row (#6713):
 *
 * - `neutral` — at rest, or over: Draft, Expired, Declined;
 * - `info` — moving, with someone else: Sent, In negotiation, Pending;
 * - `warning` — waiting on the reader or their side: Awaiting approval, Needs review;
 * - `success` — settled the way it was meant to: Accepted, Hired.
 *
 * Four, so a reader learns them once. Red is left out on purpose: a status is not an error.
 */
export type StatusTone = "neutral" | "info" | "warning" | "success";

type StatusChipProps = {
  label: string;
  tone?: StatusTone;
};

const TonedChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "tone" })<{ tone: StatusTone }>(
  ({ theme, tone }) => {
    const palette = tone === "neutral" ? undefined : theme.palette[tone];
    return {
      maxWidth: "100%",
      height: 22,
      borderRadius: 11,
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.caption.fontSize,
      color: palette ? palette.dark : theme.palette.text.primary,
      backgroundColor: palette ? alpha(palette.main, 0.12) : theme.palette.action.selected,
      "& .MuiChip-label": { paddingInline: theme.spacing(1), minWidth: 0 },
    };
  },
);

/**
 * A row's status, as one chip in one of four tones (#6713). It replaces the plain text, the outlined
 * chips and the colored words lists drew for the same idea. A label longer than its column ends in an
 * ellipsis and shows the whole of itself on hover.
 */
export function StatusChip(props: Readonly<StatusChipProps>) {
  const tone = props.tone ?? "neutral";
  return (
    <TonedChip
      tone={tone}
      data-tone={tone}
      size="small"
      label={<TruncatedCell component="span">{props.label}</TruncatedCell>}
    />
  );
}
